import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";

// Public (no login): worker taps "I didn't work that week" from a reminder email/text.
const Shell = ({ children }) => (
  <div className="min-h-screen bg-slate-100 flex items-start justify-center p-4 pt-10">
    <div className="bg-white rounded-lg shadow-lg border-t-4 border-blue-600 w-full max-w-md p-6" data-testid="no-work-public-card">
      <div className="flex items-center gap-2 mb-3">
        <img src={`${API_BASE}/settings/company/logo`} alt="SOS" className="h-8 w-8 rounded object-contain" />
        <div className="text-xs uppercase tracking-widest text-slate-400 font-bold">Safety Onsite Solutions</div>
      </div>
      {children}
    </div>
  </div>
);

export default function NoWorkPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [checked, setChecked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/public/no-work/${token}`)
      .then((r) => {
        setData(r.data);
        const open = r.data.assignments.filter((a) => !a.has_timesheet);
        // One open assignment → pre-check it so it's a single tap.
        setChecked(new Set(open.length === 1 ? [open[0].id] : open.filter((a) => a.no_work).map((a) => a.id)));
      })
      .catch((e) => setErr(e.response?.data?.detail?.toString() || "This link is not valid."));
  }, [token]);

  const toggle = (id) => setChecked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/public/no-work/${token}`, { assignment_ids: [...checked] });
      setDone(r.data);
    } catch (e) { setErr(e.response?.data?.detail?.toString() || e.message); }
    finally { setBusy(false); }
  };


  if (err && !data) return <Shell><div data-testid="no-work-public-error" className="text-rose-600 font-semibold">{err}</div></Shell>;
  if (!data) return <Shell><div className="text-slate-500">Loading…</div></Shell>;

  if (done) {
    return (
      <Shell>
        <div data-testid="no-work-public-done">
          <h1 className="text-xl font-black text-blue-700">✓ Got it — thanks</h1>
          <p className="mt-2 text-sm text-slate-700">
            We've noted that you didn't work the week of <b>{done.week_pretty}</b> on <b>{done.project_names.join(", ")}</b>.
            You won't get any more reminders for that week.
          </p>
          <p className="mt-3 text-xs text-slate-500">Worked after all? Just sign in and submit a timesheet — it always takes priority.</p>
        </div>
      </Shell>
    );
  }

  const open = data.assignments.filter((a) => !a.has_timesheet);
  return (
    <Shell>
      <h1 className="text-xl font-black text-slate-900">Hi {data.worker_name.split(" ")[0]} — no work last week?</h1>
      <p className="mt-2 text-sm text-slate-600">Week of <b>{data.week_pretty}</b>. Confirm the assignment(s) you had <b>no hours</b> on and we'll stop the reminders.</p>
      {data.assignments.length === 0 && <div className="mt-4 text-sm text-slate-500">You have no active assignments for that week.</div>}
      <div className="mt-4 space-y-2">
        {data.assignments.map((a) => (
          <label key={a.id} data-testid={`no-work-public-option-${a.id}`}
            className={`flex items-start gap-3 border rounded-md p-3 ${a.has_timesheet ? "opacity-60 bg-slate-50" : "cursor-pointer"} ${checked.has(a.id) && !a.has_timesheet ? "border-blue-400 bg-blue-50" : "border-slate-200"}`}>
            <input type="checkbox" className="mt-1 h-4 w-4 accent-blue-600" disabled={a.has_timesheet}
              checked={a.has_timesheet ? false : checked.has(a.id)} onChange={() => toggle(a.id)}
              data-testid={`no-work-public-check-${a.id}`} />
            <div>
              <div className="font-semibold">{a.project_name}</div>
              {a.client_name && <div className="text-xs text-slate-500">{a.client_name}</div>}
              {a.has_timesheet && <div className="text-xs text-emerald-700 font-semibold mt-1">Timesheet already submitted — nothing to do</div>}
            </div>
          </label>
        ))}
      </div>
      {err && <div className="mt-3 text-sm text-rose-600 font-semibold" data-testid="no-work-public-error">{err}</div>}
      {open.length > 0 && (
        <button data-testid="no-work-public-submit" disabled={busy || checked.size === 0} onClick={submit}
          className="mt-5 w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-md disabled:opacity-50">
          {busy ? "Saving…" : "I did not work last week"}
        </button>
      )}
    </Shell>
  );
}
